import { applyPermissions, selectUsers } from 'actions/userPermissionsPage';
import EnvelopeCollapsableSection from 'components/CollapsableSection/EnvelopeCollapsableSection';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCanApply,
  getExcludedRoles,
  getIncludedRoles,
  getIsSelectAll,
  getSelectedUsers,
} from 'selectors/userPermissionsPage';
import { getUsersTotalCount } from 'selectors/users';
import SelectedUsersSection from './SelectedUsersSection';
import UsersChangePermissions from './UsersChangePermissions';
import UsersInformationSection from './UsersInformationSection';
import UsersStats from './UsersStats';

type ComponentProps = {
  footer?: boolean;
  saveInApply?: boolean;
  shouldDeleteUser?: boolean;
  isMainUsersPage: boolean;
  renderExtraOptions?: boolean;
};

const MultipleUserSidebar: React.FC<ComponentProps> = ({
  footer = true,
  saveInApply = false,
  shouldDeleteUser = false,
  isMainUsersPage,
  renderExtraOptions = true,
}) => {
  const dispatch = useDispatch();

  const selectedUsers = useSelector(getSelectedUsers);
  const isSelectAll = useSelector(getIsSelectAll);
  const usersCount = useSelector(getUsersTotalCount);
  const canApply = useSelector(getCanApply);

  const [sendInvitation, setSendInvitation] = useState(true);

  const includedRoles = useSelector(getIncludedRoles);
  const excludedRoles = useSelector(getExcludedRoles);

  const [canSendInvitation, setCanSendInvitation] = useState(false);

  useEffect(() => {
    const reviewerRoles = [
      'super-reviewer',
      'legal-user',
      'reviewer',
      'super-user',
      'system-admin',
    ];
    const canSend = selectedUsers.every((user) =>
      user.roles.some((role) => reviewerRoles.includes(role))
    );
    setCanSendInvitation(canSend);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUsers.length, includedRoles, excludedRoles]);

  const handleApply = (): void => {
    dispatch(applyPermissions());
  };

  const cancelEdit = (): void => {
    dispatch(selectUsers([]));
  };

  return (
    <div className="relative h-full flex flex-col bg-white">
      <div className="flex-1 overflow-auto no-scrollbar">
        <div className="h-full">
          <EnvelopeCollapsableSection
            headerTitle={`Selected Users (${isSelectAll ? usersCount : selectedUsers.length})`}
          >
            <SelectedUsersSection
              shouldRemoveUsers={!saveInApply}
              canSendInvitation={canSendInvitation && isMainUsersPage}
              renderExtraOptions={renderExtraOptions}
            />
          </EnvelopeCollapsableSection>
          <EnvelopeCollapsableSection headerTitle="User Details" isDefaultClosed>
            <UsersStats />
          </EnvelopeCollapsableSection>
          <EnvelopeCollapsableSection
            headerTitle="Bulk User Permissions"
            bulkReview={selectedUsers.length > 1}
          >
            <>
              <UsersInformationSection users={selectedUsers} shouldDeleteUser={shouldDeleteUser} />
              <UsersChangePermissions
                saveInApply={saveInApply}
                sendInvitation={sendInvitation}
                setSendInvitation={setSendInvitation}
                canSendInvitation={canSendInvitation && isMainUsersPage}
              />
            </>
          </EnvelopeCollapsableSection>
        </div>
      </div>

      {footer && (
        <div
          className="flex flex-col gap-1 bg-white border border-litlingo-gray-1 p-4"
          style={{ filter: 'drop-shadow(3px 1px 8px rgba(0, 0, 0, 0.25))' }}
        >
          <div className="flex flex-row items-center justify-between gap-6 h-8">
            <button
              type="button"
              className="button button--secondary w-27 py-1.5 h-full"
              onClick={cancelEdit}
            >
              <div className="flex w-full justify-center items-center font-bold">Cancel</div>
            </button>

            <button
              type="button"
              id="next-envelope-button"
              data-testid="next-envelope-button"
              className="button  button--primary min-w-27 h-full py-1.5 focus:outline-none"
              onClick={handleApply}
              disabled={!canApply}
            >
              <div className="flex w-full justify-center items-center text-base font-bold leading-5">
                Apply
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
export default MultipleUserSidebar;
